import { createSelector } from 'reselect'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { getTourV2Items } from '../view/toursv2'
import { getCruiseItems } from '../cruiseSelectors'

const DISABLE_KEY = 'disable_service_fee'

const isTourUsingDefaultDeposit = (offer?: { depositType?: string | null }) =>
  !offer?.depositType || offer.depositType === 'default'

export const getDefaultDepositAmountPercentage = createSelector(
  (state: App.State) => state.system.depositConfigs,
  (state: App.State) => getOptimizelyExperimentVariation(state, Experiments.paymentsDepositOptionForHotelsAbove1999V2.id),
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.tourV2Offers,
  (depositConfigs, depositVariation, tourItems, tourV2Offers): number => {
    if (!depositConfigs) return 0

    const tourItem = tourItems[0]
    if (tourItem !== undefined) {
      const offer = tourV2Offers?.[tourItem.offerId]
      if (isTourUsingDefaultDeposit(offer)) {
        return depositConfigs.TOUR_DEPOSIT_PERCENTAGE ?? 25
      }
    }

    if (depositVariation) {
      return depositConfigs.ABOVE_1999_DEPOSIT_PERCENTAGE_OVERRIDE
    }
    return depositConfigs.DEPOSIT_AMOUNT_PERCENTAGE
  },
)

export const getDepositServiceFee = createSelector(
  (state: App.State) => state.system.depositConfigs,
  (state: App.State) => getOptimizelyExperimentVariation(state, Experiments.paymentsDepositOptionForHotelsAbove1999V2.id),
  (depositConfigs, depositVariation): number => {
    if (!depositConfigs) return 0
    if (depositVariation) {
      return depositConfigs.ABOVE_1999_SERVICE_FEE_PERCENTAGE_OVERRIDE
    }
    return depositConfigs.SERVICE_FEE_PERCENTAGE
  },
)

export const getDepositServiceFeeConfig = createSelector(
  (state: App.State) => getDepositServiceFee(state),
  (state: App.State) => state.system.originalCanonicalURL,
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => !!getCruiseItems(state).length,
  (state: App.State) => state.offer.tourV2Offers,
  (state: App.State) => state.system.depositConfigs,
  (state: App.State) => state.checkout.cart.currencyCode,
  (depositServiceFee, url = '', tourItems, hasCruiseItems, tourV2Offers, depositConfigs, currencyCode): App.Checkout.ServiceFeeSettings => {
    const queryString = url.slice(url.indexOf('?') + 1)
    const params = new URLSearchParams(queryString)
    const isEnabled = params.get(DISABLE_KEY) !== 'true'

    const tourItem = tourItems[0]
    if (tourItem !== undefined && tourV2Offers?.[tourItem.offerId]?.productType === 'connection_tour') {
      return {
        isEnabled: false,
        percentage: 0,
      }
    }

    if (tourItem !== undefined) {
      const offer = tourV2Offers?.[tourItem.offerId]
      if (isTourUsingDefaultDeposit(offer)) {
        const flatAmounts = depositConfigs?.TOUR_SERVICE_FEE_FLAT_AMOUNTS
        const flatAmount = flatAmounts?.[currencyCode] ?? flatAmounts?.AUD ?? 199
        return {
          isEnabled: isEnabled && flatAmount > 0,
          percentage: 0,
          type: 'fixed_amount' as const,
          flatAmount,
        }
      }
    }

    if (hasCruiseItems) {
      const cruiseServiceFeePercentage = depositConfigs?.CRUISE_SERVICE_FEE_PERCENTAGE ?? 0

      return {
        isEnabled: isEnabled && (cruiseServiceFeePercentage !== 0),
        percentage: cruiseServiceFeePercentage,
      }
    }

    if (config.agentHub.isEnabled) {
      const agentHubServiceFeePercentage = depositConfigs?.AGENTHUB_SERVICE_FEE_PERCENTAGE ?? 0

      return {
        isEnabled: isEnabled && (Number(agentHubServiceFeePercentage) !== 0),
        percentage: agentHubServiceFeePercentage,
      }
    }

    return {
      isEnabled: isEnabled && (Number(depositServiceFee) !== 0),
      percentage: depositServiceFee,
    }
  },
)
